/**
 * EllipseAnnotation — renders an ellipse with independent radiusX / radiusY.
 *
 * Supports configurable lineWidth, color, and optional fill.
 * Interactive: four directional edge handles (N/S/E/W) for per-axis resize,
 * body drag to move the whole shape.
 */

import { distSq } from '../geometry';
import type {
    AnnotationInteractionEvent,
    AnnotationStyle,
    EllipseAnnotationData,
    IAnnotationObject,
} from '../types';
import { AnnotationType, DEFAULT_ANNOTATION_STYLE } from '../types';

const EDGE_TOLERANCE = 8;
const HANDLE_RADIUS = 6;
const MIN_RADIUS = 5;

type EdgeHandle = 'edge-n' | 'edge-s' | 'edge-e' | 'edge-w';
type EllipseHandle = EdgeHandle | 'body';

interface EllipseDragState {
    handle: EllipseHandle;
    startCenter: { x: number; y: number };
    startRadiusX: number;
    startRadiusY: number;
}

export class EllipseAnnotation implements IAnnotationObject {
    readonly id: string;
    readonly interactive = true;
    visible = true;
    selected = false;
    readonly zIndex: number;

    position: { x: number; y: number };
    center: { x: number; y: number };
    radiusX: number;
    radiusY: number;
    style: AnnotationStyle;
    fillColor: string | null;
    fillOpacity: number;

    hoveredHandle: EllipseHandle | null = null;

    private dragState: EllipseDragState | null = null;

    constructor(options: {
        id: string;
        center: { x: number; y: number };
        radiusX: number;
        radiusY: number;
        style?: Partial<AnnotationStyle>;
        fillColor?: string;
        fillOpacity?: number;
        zIndex?: number;
    }) {
        this.id = options.id;
        this.center = { ...options.center };
        this.radiusX = options.radiusX;
        this.radiusY = options.radiusY;
        this.position = { ...options.center };
        this.style = { ...DEFAULT_ANNOTATION_STYLE, ...options.style };
        this.fillColor = options.fillColor ?? null;
        this.fillOpacity = options.fillOpacity ?? 0.1;
        this.zIndex = options.zIndex ?? 100;
    }

    // ── Hit testing ──

    hitTest(x: number, y: number): boolean {
        return this.getHandleAtPoint(x, y) !== null;
    }

    getHandleAtPoint(x: number, y: number): EllipseHandle | null {
        // Check edge handles first (small circles at cardinal points)
        const edgeHandle = this.hitTestEdgeHandle(x, y);
        if (edgeHandle) return edgeHandle;

        // Check ellipse perimeter (for edge-ish hits not near a handle)
        if (this.hitTestPerimeter(x, y)) {
            return this.nearestEdgeHandle(x, y);
        }

        // Check interior (body move)
        if (this.hitTestInterior(x, y)) return 'body';

        return null;
    }

    private hitTestEdgeHandle(x: number, y: number): EdgeHandle | null {
        const handles = this.getEdgeHandlePositions();
        const threshold = (HANDLE_RADIUS + 2) ** 2;

        for (const [handle, pos] of handles) {
            if (distSq(x, y, pos.x, pos.y) <= threshold) {
                return handle;
            }
        }
        return null;
    }

    private hitTestPerimeter(x: number, y: number): boolean {
        if (this.radiusX < 1 || this.radiusY < 1) return false;
        const nx = (x - this.center.x) / this.radiusX;
        const ny = (y - this.center.y) / this.radiusY;
        const normalizedDist = Math.sqrt(nx * nx + ny * ny);
        const tolerance = (EDGE_TOLERANCE + this.style.lineWidth / 2) / Math.min(this.radiusX, this.radiusY);
        return Math.abs(normalizedDist - 1) <= tolerance;
    }

    private hitTestInterior(x: number, y: number): boolean {
        if (this.radiusX < 1 || this.radiusY < 1) return false;
        const nx = (x - this.center.x) / this.radiusX;
        const ny = (y - this.center.y) / this.radiusY;
        return nx * nx + ny * ny < 1;
    }

    private nearestEdgeHandle(x: number, y: number): EdgeHandle {
        const handles = this.getEdgeHandlePositions();
        let best: EdgeHandle = 'edge-e';
        let bestDist = Infinity;
        for (const [handle, pos] of handles) {
            const d = distSq(x, y, pos.x, pos.y);
            if (d < bestDist) {
                bestDist = d;
                best = handle;
            }
        }
        return best;
    }

    private getEdgeHandlePositions(): [EdgeHandle, { x: number; y: number }][] {
        return [
            ['edge-e', { x: this.center.x + this.radiusX, y: this.center.y }],
            ['edge-w', { x: this.center.x - this.radiusX, y: this.center.y }],
            ['edge-n', { x: this.center.x, y: this.center.y - this.radiusY }],
            ['edge-s', { x: this.center.x, y: this.center.y + this.radiusY }],
        ];
    }

    // ── Interaction ──

    clearHoverState(): void {
        this.hoveredHandle = null;
    }

    handleEvent(event: AnnotationInteractionEvent): boolean {
        switch (event.type) {
            case 'hover': {
                const handle = this.getHandleAtPoint(event.currentPoint.x, event.currentPoint.y);
                if (handle) {
                    this.hoveredHandle = handle;
                    return true;
                }
                return false;
            }
            case 'pointer_down': {
                const handle = this.getHandleAtPoint(event.currentPoint.x, event.currentPoint.y);
                if (!handle) return false;
                this.dragState = {
                    handle,
                    startCenter: { ...this.center },
                    startRadiusX: this.radiusX,
                    startRadiusY: this.radiusY,
                };
                return true;
            }
            case 'pointer_move': {
                if (!this.dragState) return false;
                const dx = event.currentPoint.x - event.startPoint.x;
                const dy = event.currentPoint.y - event.startPoint.y;
                const { handle, startCenter, startRadiusX, startRadiusY } = this.dragState;

                switch (handle) {
                    case 'edge-e':
                        this.radiusX = Math.max(MIN_RADIUS, startRadiusX + dx);
                        break;
                    case 'edge-w':
                        this.radiusX = Math.max(MIN_RADIUS, startRadiusX - dx);
                        break;
                    case 'edge-s':
                        this.radiusY = Math.max(MIN_RADIUS, startRadiusY + dy);
                        break;
                    case 'edge-n':
                        this.radiusY = Math.max(MIN_RADIUS, startRadiusY - dy);
                        break;
                    case 'body':
                        this.center = { x: startCenter.x + dx, y: startCenter.y + dy };
                        this.position = { ...this.center };
                        break;
                }
                return true;
            }
            case 'pointer_up': {
                this.dragState = null;
                return true;
            }
        }
    }

    // ── Placement ──

    updatePlacement(startPoint: { x: number; y: number }, currentPoint: { x: number; y: number }): void {
        this.center = {
            x: (startPoint.x + currentPoint.x) / 2,
            y: (startPoint.y + currentPoint.y) / 2,
        };
        this.radiusX = Math.abs(currentPoint.x - startPoint.x) / 2;
        this.radiusY = Math.abs(currentPoint.y - startPoint.y) / 2;
        this.position = { ...this.center };
    }

    isValidPlacement(): boolean {
        return this.radiusX >= 3 || this.radiusY >= 3;
    }

    updateStyle(patch: Partial<AnnotationStyle>): void {
        Object.assign(this.style, patch);
    }

    // ── Serialisation ──

    serialize(): EllipseAnnotationData {
        return {
            type: AnnotationType.CIRCLE,
            id: this.id,
            center: { ...this.center },
            radiusX: this.radiusX,
            radiusY: this.radiusY,
            style: { ...this.style },
        };
    }

    static hydrate(data: EllipseAnnotationData): EllipseAnnotation {
        return new EllipseAnnotation({
            id: data.id,
            center: data.center,
            radiusX: data.radiusX,
            radiusY: data.radiusY,
            style: data.style,
        });
    }

    // ── Rendering ──

    render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || (this.radiusX < 1 && this.radiusY < 1)) return;

        const { center, radiusX, radiusY, style } = this;

        ctx.save();

        // Fill
        if (this.fillColor) {
            ctx.beginPath();
            ctx.ellipse(center.x, center.y, radiusX, radiusY, 0, 0, Math.PI * 2);
            ctx.globalAlpha = this.fillOpacity;
            ctx.fillStyle = this.fillColor;
            ctx.fill();
            ctx.globalAlpha = 1;
        }

        // Stroke
        ctx.beginPath();
        ctx.ellipse(center.x, center.y, radiusX, radiusY, 0, 0, Math.PI * 2);
        ctx.strokeStyle = style.color;
        ctx.lineWidth = style.lineWidth;
        ctx.stroke();

        // Selection takes precedence: always show the resize handles.
        if (this.selected || this.hoveredHandle?.startsWith('edge')) {
            this.renderEdgeHandles(ctx);
        } else if (this.hoveredHandle === 'body') {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(center.x - radiusX, center.y - radiusY, radiusX * 2, radiusY * 2);
        }

        ctx.restore();
    }

    private renderEdgeHandles(ctx: CanvasRenderingContext2D): void {
        for (const [, pos] of this.getEdgeHandlePositions()) {
            ctx.beginPath();
            ctx.arc(pos.x, pos.y, HANDLE_RADIUS, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.fill();
            ctx.strokeStyle = this.style.color;
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }
    }
}
